import {Box} from '@mui/material';
import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {FC, useState} from 'react';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {TariffSearchRequest} from 'src/types';
import {TariffTableProps} from './TariffsTable.types';
import {columnConfigTariffs} from './TariffsTable.tableConfig';
import {TariffsFilterValue} from '../TariffsTableActions/TariffsTableActions.types';
import {TariffsTableActions} from '../TariffsTableActions';
import {TariffTableCard} from '../TariffTableCard';
import {useGetUserLanguage} from '../../../hooks/useGetUserLanguage';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';

export const TariffsTable: FC<TariffTableProps> = ({
                                                       data,
                                                       size,
                                                       pageNumber,
                                                       handleUpdateTableData,
                                                       handleChangePage,
                                                       handleChangeRowsNumber,
                                                       title,
                                                       onAddClick,
                                                       buttonText,
                                                       onlyTitle,
                                                       icon,
                                                       filters,
                                                   }) => {
    // Seeded from the filter the page holds, not from empty: a remount is what coming back from a
    // detail page is, and starting at '' meant the next filter change wiped the search that was
    // applied - `updateData` writes `keyword: search`, so an empty local copy cleared the URL.
    const [searchValue, setSearchValue] = useState<string>(filters.keyword ?? '');
    const [sortValue, setSortValue] = useState<GridSortItem>({field: '', sort: 'asc'});
    const [currentLanguage] = useGetUserLanguage();

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue, filters));
    };

    const handleFilter = (value: TariffsFilterValue) => {
        handleUpdateTableData(updateData(searchValue, sortValue, {...filters, ...value}));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0], filters));
    };

    /**
     * The filter the list is asked for: a merge onto what it is already on, not a fresh object.
     *
     * Built from scratch, it carried only the toolbar's own fields - so the status chosen on the
     * strip above was quietly gone as soon as anything here changed.
     */
    const updateData = (search: string, sort: GridSortItem, filter: TariffSearchRequest) => ({
        ...filter,
        keyword: search,
        sortAsc: sort ? sort.sort === 'asc' : true,
        sortField: sort && sort.field,
    });

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <TariffsTableActions
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        handleSearch={handleSearch}
                        handleFilter={handleFilter}
                        filters={filters}
                        totalElements={data.page.totalElements}
                    />
                )}
                totalPages={data.page.totalPages}
                page={pageNumber && pageNumber === 0 ? pageNumber : data.page.number}
                pageSize={size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
            <ResponsiveTable
                desktopElement={
                    <Table
                        rows={data.content}
                        hideFooter
                        checkboxSelection={false}
                        columns={columnConfigTariffs(currentLanguage)()}
                        handleSort={handleSort}
                    />
                }
                responsiveElement={
                    <CardList>
                        {data?.content?.map((item) => (
                            <TariffTableCard
                                key={item?.id}
                                {...item}
                            />
                        ))}
                    </CardList>
                }
            />
            </TableLayout>
        </Box>
    );
};
